import { supabase } from '@/integrations/supabase/client';

export type UpvoteTarget = 'display' | 'sequence';

export async function getUserUpvoted(
  target: UpvoteTarget,
  id: string | number,
  userId?: string | null
): Promise<boolean> {
  if (!userId) return false;
  
  const table = target === 'display' ? 'display_upvotes' : 'sequence_upvotes';
  const col = target === 'display' ? 'display_id' : 'sequence_id';
  
  const { data, error } = await supabase
    .from(table)
    .select('id')
    .eq(col, id)
    .eq('user_id', userId)
    .single();
    
  if (error) {
    // Treat errors as not upvoted (including no rows found)
    return false;
  }
  
  return !!data;
}

export async function getUpvoteCount(
  target: UpvoteTarget,
  id: string | number
): Promise<number> {
  // Prefer cached counters if present; fall back to counting upvotes
  try {
    if (target === 'display') {
      const { data, error } = await supabase
        .from('displays')
        .select('upvote_count')
        .eq('id', id)
        .single();
      if (!error && data && typeof (data as any).upvote_count === 'number') {
        return (data as any).upvote_count as number;
      }
    } else {
      const { data, error } = await supabase
        .from('sequences')
        .select('upvote_count')
        .eq('id', id)
        .single();
      if (!error && data && typeof (data as any).upvote_count === 'number') {
        return (data as any).upvote_count as number;
      }
    }
  } catch {}

  // Fallback: count rows in the appropriate upvotes table
  const table = target === 'display' ? 'display_upvotes' : 'sequence_upvotes';
  const col = target === 'display' ? 'display_id' : 'sequence_id';
  
  const { count } = await supabase
    .from(table)
    .select('id', { count: 'exact', head: true })
    .eq(col, id);
  return count || 0;
}

export async function toggleUpvote(
  target: UpvoteTarget,
  id: string | number,
  userId: string
): Promise<'added' | 'removed'> {
  // Use the baseline-preserving RPC functions we created for atomic toggle operations
  const functionName = target === 'display' ? 'toggle_display_upvote_with_baseline' : 'toggle_sequence_upvote_with_baseline';
  const paramName = target === 'display' ? 'p_display_id' : 'p_sequence_id';
  
  const { data, error } = await supabase.rpc(functionName, {
    [paramName]: id
  });
  
  if (error) {
    throw error;
  }
  
  // The RPC function returns { upvoted: boolean, count: number }
  return data?.upvoted ? 'added' : 'removed';
}


